import React from 'react';
import { useBrand } from '../contexts/BrandContext';
import { BrandName } from '../components/Common/BrandedText';
import {
  RocketLaunchIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const OnboardingWelcome = ({ onStartOnboarding, onSkip }) => {
  const { brand } = useBrand();
  const businessLabel = brand.code === 'smbmate' ? 'service business' : 'field service business';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/20 rounded-full mb-4">
            <RocketLaunchIcon className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Welcome to <BrandName />!
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            We won't overwhelm you with complexity. 
            Get your {businessLabel} configured with a clear, guided setup.
          </p>
        </div>

        {/* Comparison with competitors */}
        <div className="bg-slate-800/60 backdrop-blur-sm rounded-xl border border-slate-700/50 shadow-lg p-8 mb-8">
          <h2 className="text-xl font-semibold text-white mb-6 text-center">
            Why <BrandName /> is Different
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Competitor 1 */}
            <div className="text-center p-4 bg-red-500/10 rounded-lg border border-red-500/30">
              <div className="text-red-400 font-medium mb-2">Large Implementations</div>
              <div className="space-y-2 text-sm text-red-300">
                <div className="flex items-center gap-2">
                  <XMarkIcon className="w-4 h-4" />
                  <span>Longer implementation projects</span>
                </div>
                <div className="flex items-center gap-2">
                  <XMarkIcon className="w-4 h-4" />
                  <span>Layered plans and add-ons</span>
                </div>
                <div className="flex items-center gap-2">
                  <XMarkIcon className="w-4 h-4" />
                  <span>More administrative overhead</span>
                </div>
                <div className="flex items-center gap-2">
                  <XMarkIcon className="w-4 h-4" />
                  <span>Support options vary</span>
                </div>
              </div>
            </div>

            {/* Competitor 2 */}
            <div className="text-center p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
              <div className="text-yellow-400 font-medium mb-2">Other Platforms</div>
              <div className="space-y-2 text-sm text-yellow-300">
                <div className="flex items-center gap-2">
                  <XMarkIcon className="w-4 h-4" />
                  <span>Different setup priorities</span>
                </div>
                <div className="flex items-center gap-2">
                  <XMarkIcon className="w-4 h-4" />
                  <span>Features spread across tools</span>
                </div>
                <div className="flex items-center gap-2">
                  <XMarkIcon className="w-4 h-4" />
                  <span>Separate upgrade decisions</span>
                </div>
                <div className="flex items-center gap-2">
                  <XMarkIcon className="w-4 h-4" />
                  <span>More systems to maintain</span>
                </div>
              </div>
            </div>

            {/* Current Brand */}
            <div className="text-center p-4 bg-green-500/10 rounded-lg border border-green-500/30">
              <div className="text-green-400 font-medium mb-2"><BrandName /></div>
              <div className="space-y-2 text-sm text-green-300">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Guided setup</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Better value pricing</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Simple yet powerful</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Email support</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Setup options */}
        <div className="bg-slate-800/60 backdrop-blur-sm rounded-xl border border-slate-700/50 shadow-lg p-8 mb-8">
          <h2 className="text-xl font-semibold text-white mb-6 text-center">
            How Setup Works
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Quick Start */}
            <div className="border-2 border-blue-500/40 rounded-lg p-6 hover:border-blue-400 transition-colors cursor-pointer bg-slate-700/30"
                 onClick={() => onStartOnboarding()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                  ⚡
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">Quick Start</h3>
                  <div className="flex items-center gap-2 text-sm text-blue-400">
                    <ClockIcon className="w-4 h-4" />
                    <span>Essentials only</span>
                  </div>
                </div>
              </div>
              <p className="text-slate-300 mb-4">
                Perfect for getting started immediately. Just the essentials: company info, 
                one service, and you're ready to create your first quote.
              </p>
              <div className="space-y-2 text-sm text-slate-300">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-green-400" />
                  <span>Company basics</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-green-400" />
                  <span>One service setup</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-green-400" />
                  <span>Ready to create your first quote</span>
                </div>
              </div>
            </div>

            {/* Complete Setup */}
            <div className="border-2 border-slate-600/50 rounded-lg p-6 hover:border-slate-500 transition-colors cursor-pointer bg-slate-700/30"
                 onClick={() => onStartOnboarding()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-slate-600/30 rounded-full flex items-center justify-center">
                  ⚙️
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">Complete Setup</h3>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <ClockIcon className="w-4 h-4" />
                    <span>All settings</span>
                  </div>
                </div>
              </div>
              <p className="text-slate-300 mb-4">
                Full configuration with team setup, business preferences, financial settings, 
                and more, all in one guided flow.
              </p>
              <div className="space-y-2 text-sm text-slate-300">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-green-400" />
                  <span>All 6 setup steps</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-green-400" />
                  <span>Team member invites</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-green-400" />
                  <span>Full business configuration</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features highlight */}
        <div className="bg-slate-800/60 backdrop-blur-sm rounded-xl border border-slate-700/50 shadow-lg p-8 mb-8">
          <h2 className="text-xl font-semibold text-white mb-6 text-center">
            What Makes Our Setup Special
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <ShieldCheckIcon className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="font-medium text-white mb-2">Auto-Save Progress</h3>
              <p className="text-sm text-slate-400">
                Never lose your setup progress. We automatically save every 30 seconds.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-medium text-white mb-2">Email Support</h3>
              <p className="text-sm text-slate-400">
                Get setup help from our team at {brand.supportEmail}.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <RocketLaunchIcon className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="font-medium text-white mb-2">Start With a Clean Workspace</h3>
              <p className="text-sm text-slate-400">
                Add your own customers and create your first quote when you are ready.
              </p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="text-center">
          <div className="space-y-4">
            {/* ONE call to action. Offering "Quick" and "Complete" here asked the user to
                pick a path before they had seen anything; the next screen asks two concrete
                questions and picks for them, with a one-click override. */}
            <div>
              <button
                onClick={() => onStartOnboarding()}
                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors text-lg"
              >
                <RocketLaunchIcon className="w-6 h-6" />
                Get Started
              </button>
            </div>
            
            <div>
              <button
                onClick={onSkip}
                className="text-slate-400 hover:text-slate-300 text-sm"
              >
                Skip setup for now
              </button>
            </div>
          </div>
          
          <div className="mt-6 text-sm text-slate-500">
            💡 You can always complete the full setup later from Settings
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWelcome;
